import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Field } from '@spark-web/field';
import { CheckIcon, ExclamationCircleIcon } from '@spark-web/icon';
import { Inline } from '@spark-web/inline';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { InputAdornment, TextInput } from '@spark-web/text-input';
import { TextLinkButton } from '@spark-web/text-link';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetcher } from 'react-router';

import { RULE_MOBILE_NO_PATTERN } from '~/constants';
import { AUTH_ERROR_MESSAGES } from '~/constants/auth';
import type { action as sendMobileCodeAction } from '~/routes/api+/auth+/send-mobile-code';
import type { action as verifyMobileCodeAction } from '~/routes/api+/auth+/verify-mobile-code';
import { CustomerFormValues } from '~/types/marketplace';
import { setMobileVerifiedToken } from '~/utils/localstorage';

const verifyReasons = [
  'To share correct contact details with your selected vendors',
  'You will not receive any unsolicited calls',
];

export const MobileVerificationInput = () => {
  const { control, setValue, watch, resetField } = useFormContext<CustomerFormValues>();

  const sendCodeFetcher = useFetcher<typeof sendMobileCodeAction>();
  const verifyCodeFetcher = useFetcher<typeof verifyMobileCodeAction>();
  const verifyError =
    verifyCodeFetcher.data && 'error' in verifyCodeFetcher.data ? verifyCodeFetcher.data.error : undefined;

  const [sentVerify, setSentVerify] = useState<boolean>(false);
  const [showVerify, setShowVerify] = useState<boolean>(false);

  const [sentCode, setSentCode] = useState<boolean>(false);
  const [secureCode, setSecureCode] = useState<string>('');

  const phoneVerified = watch('phoneVerified');
  const phone = watch('phone');
  const email = watch('email');

  /** Send code if phone is not verified, code is not sent and valid phone */
  useEffect(() => {
    const validPhone = RULE_MOBILE_NO_PATTERN.pattern.value.test(phone) && !phoneVerified;
    setShowVerify(validPhone);

    if (validPhone && !sentCode) {
      sendCodeFetcher.submit(
        { phone, email },
        {
          method: 'POST',
          action: '/api/auth/send-mobile-code',
        },
      );

      setSentCode(true);
    }
  }, [phoneVerified, sentCode, sendCodeFetcher, phone, email]);

  /** On verify code success, set phoneVerified to true, save token to localStorage, and reset form and state */
  useEffect(() => {
    if (sentVerify && verifyCodeFetcher.state === 'idle' && !verifyError) {
      setValue('phoneVerified', true);

      const tokenData =
        verifyCodeFetcher.data && 'token' in verifyCodeFetcher.data ? verifyCodeFetcher.data.token : undefined;
      if (tokenData) {
        setMobileVerifiedToken(tokenData, phone);
      }

      reset();
    }
  }, [verifyError, sentVerify, verifyCodeFetcher, setValue, phone]);

  const verifyCode = (value: string) => {
    if (value.length === 6 && !phoneVerified) {
      verifyCodeFetcher.submit(
        { code: value, phone, email },
        {
          method: 'POST',
          action: '/api/auth/verify-mobile-code',
        },
      );

      setSentVerify(true);
    }
  };

  const changeNumber = () => {
    resetField('phone');
    resetField('phoneVerified');

    reset();
  };

  const reset = () => {
    setSecureCode('');
    setSentCode(false);
    setSentVerify(false);
    setShowVerify(false);
  };

  return (
    <Stack gap="large">
      <Controller
        control={control}
        rules={{ required: true, ...RULE_MOBILE_NO_PATTERN }}
        name="phone"
        render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
          <Field
            label="Mobile number"
            tone={fieldState?.error ? 'critical' : 'neutral'}
            message={fieldState?.error?.message}
            disabled={phoneVerified}
          >
            <TextInput
              value={value ?? ''}
              onBlur={onBlur}
              onChange={(e) => {
                onChange(e);
                setValue('phoneVerified', false);
                setSentCode(false);
                setSentVerify(false);
              }}
              name={name}
            >
              {phoneVerified ? (
                <InputAdornment placement="start">
                  <CheckIcon size="xsmall" tone="positive" />
                </InputAdornment>
              ) : null}
            </TextInput>
            <Text size="xsmall">
              The vendors will contact you on this number{' '}
              {phoneVerified ? <TextLinkButton onClick={changeNumber}>Change number?</TextLinkButton> : null}
            </Text>
          </Field>
        )}
      />

      {showVerify ? (
        <Box background="infoLight" gap="large" borderLeft="info" borderWidth="large">
          <Box gap="large" padding="large">
            <Stack gap="medium">
              <Text weight="bold" size="standard">
                We sent you a verification code
              </Text>
              <Text size="small">Enter the 6-digit code that we sent to your mobile.</Text>

              <Box css={css({ maxWidth: '200px' })} display="flex" flexDirection="row">
                <Controller
                  control={control}
                  name="phoneVerified"
                  rules={{
                    required: true,
                    minLength: { value: 6, message: 'Code must be 6 digits' },
                    maxLength: { value: 6, message: 'Code must be 6 digits' },
                  }}
                  render={({ field: { name }, fieldState }) => (
                    <Field
                      label="Verification code"
                      labelVisibility="hidden"
                      tone={fieldState?.error ? 'critical' : 'neutral'}
                      message={fieldState?.error?.message}
                    >
                      <TextInput
                        onChange={(e) => {
                          setSecureCode(e.target.value);
                          verifyCode(e.target.value);
                        }}
                        value={secureCode}
                        name={name}
                      />
                    </Field>
                  )}
                />
                {verifyCodeFetcher.state === 'submitting' || verifyCodeFetcher.state === 'loading' ? (
                  <Stack alignSelf="center">
                    <Spinner />
                  </Stack>
                ) : null}
              </Box>

              {verifyError ? (
                <Inline gap="small" alignY="center">
                  <ExclamationCircleIcon tone="critical" size="xsmall" />
                  <Text tone="critical" weight="semibold">
                    {verifyError in AUTH_ERROR_MESSAGES
                      ? AUTH_ERROR_MESSAGES[verifyError]
                      : AUTH_ERROR_MESSAGES['unknown_error']}
                  </Text>
                </Inline>
              ) : null}

              <Text weight="bold" size="small">
                Why do we verify your number?
              </Text>
              {verifyReasons.map((reason, index) => (
                <Box key={index} display="flex" flexDirection="row" alignItems="center" gap="xsmall">
                  <CheckIcon size="xsmall" tone="positive" />
                  <Text size="small">{reason}</Text>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};
